import React, { useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { requireAuth } from "utilities/auth";
import { BookOpen, ChevronRight, ArrowLeft } from "lucide-react";
import PageHeader from "components/PageLayout";
import Skeleton from "components/Skeleton";
import EmptyState from "components/EmptyState";
import MarkdownRenderer from "components/Wiki/MarkdownRenderer";
import {
  useWikiCategories,
  useWikiArticles,
  useWikiArticle,
} from "hooks/useWiki";

/**
 * /wiki                — index: category sidebar + article list
 * /wiki/:slug          — single article reader
 *
 * Audience gating happens server-side per `req.isInternal/isPartner/isClient`
 * — frontend just renders whatever the API returns.
 */

function WikiIndex() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("");

  const { categories, loading: catsLoading } = useWikiCategories();
  const { articles, loading: articlesLoading } = useWikiArticles({
    category: activeCategory,
  });

  const groupedByCategory = useMemo(() => {
    const grouped = {};
    for (const a of articles) {
      const c = a.category || "uncategorized";
      if (!grouped[c]) grouped[c] = [];
      grouped[c].push(a);
    }
    return grouped;
  }, [articles]);

  return (
    <div>
      <PageHeader
        title="Sound Wiki"
        subtitle="Documentation, guides, and reference for working in the Sound CRM."
      />

      <div className="container py-6">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="sticky top-6 space-y-4">
              {/* Search removed for v1 (low article count, broken UX); see SOUND-333. Hook + API support kept for re-introduction. */}
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2">
                  Categories
                </h3>
                {catsLoading ? (
                  <div className="space-y-1">
                    {[0, 1, 2, 3].map((i) => (
                      <Skeleton.Rect key={i} className="h-8 w-full" />
                    ))}
                  </div>
                ) : (
                  <ul className="space-y-0.5">
                    <li>
                      <button
                        onClick={() => setActiveCategory("")}
                        className={`w-full text-left px-2 py-1.5 rounded-md text-sm flex items-center justify-between transition-colors ${
                          activeCategory === ""
                            ? "bg-brand/15 text-brand font-medium"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        }`}
                      >
                        <span>All articles</span>
                        <span className="text-xs opacity-60">
                          {articles.length}
                        </span>
                      </button>
                    </li>
                    {categories.map((cat) => (
                      <li key={cat.category}>
                        <button
                          onClick={() => setActiveCategory(cat.category)}
                          className={`w-full text-left px-2 py-1.5 rounded-md text-sm flex items-center justify-between transition-colors ${
                            activeCategory === cat.category
                              ? "bg-brand/15 text-brand font-medium"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          }`}
                        >
                          <span>{prettifyCategory(cat.category)}</span>
                          <span className="text-xs opacity-60">{cat.count}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </aside>

          {/* Article list */}
          <div className="flex-1 min-w-0">
            {articlesLoading ? (
              <div className="space-y-3">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton.Rect key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : articles.length === 0 ? (
              <EmptyState
                icon={BookOpen}
                heading="No articles available yet"
                description="Articles will appear here as content is added."
              />
            ) : (
              <div className="space-y-8">
                {Object.entries(groupedByCategory).map(([cat, items]) => (
                  <section key={cat}>
                    {!activeCategory && (
                      <h2 className="text-lg font-semibold text-foreground mb-3">
                        {prettifyCategory(cat)}
                      </h2>
                    )}
                    <div className="space-y-2">
                      {items.map((a) => (
                        <Link
                          key={a.slug}
                          to={`/wiki/${a.slug}`}
                          className="block p-4 bg-card border border-border rounded-lg hover:bg-muted hover:border-brand/40 transition-colors group"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <h3 className="text-base font-semibold text-foreground group-hover:text-brand transition-colors">
                                {a.title}
                              </h3>
                              {a.tags?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {a.tags.map((t) => (
                                    <span
                                      key={t}
                                      className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground"
                                    >
                                      {t}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-brand transition-colors flex-shrink-0 mt-1" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function WikiArticle({ slug }) {
  const navigate = useNavigate();
  const { article, loading, error } = useWikiArticle(slug);

  if (loading) {
    return (
      <div>
        <PageHeader title="" />
        <div className="container py-6 max-w-3xl">
          <Skeleton.Rect className="h-8 w-3/4 mb-4" />
          <Skeleton.Rect className="h-4 w-full mb-2" />
          <Skeleton.Rect className="h-4 w-full mb-2" />
          <Skeleton.Rect className="h-4 w-2/3 mb-6" />
          <Skeleton.Rect className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div>
        <PageHeader
          title="Article not found"
          actions={
            <button className="btn btn-ghost" onClick={() => navigate("/wiki")}>
              <ArrowLeft className="h-4 w-4" />
              Back to Wiki
            </button>
          }
        />
        <div className="container py-6">
          <EmptyState
            icon={BookOpen}
            heading="This article isn't available."
            description="It may have been moved or you may not have access to it."
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={article.title}
        subtitle={article.category && prettifyCategory(article.category)}
        actions={
          <button className="btn btn-ghost" onClick={() => navigate("/wiki")}>
            <ArrowLeft className="h-4 w-4" />
            Back to Wiki
          </button>
        }
      />

      <div className="container py-6">
        <article className="max-w-3xl">
          <MarkdownRenderer content={article.body} />

          {article.tags?.length > 0 && (
            <div className="mt-10 pt-6 border-t border-border">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground uppercase tracking-wider mr-2">
                  Tags
                </span>
                {article.tags.map((t) => (
                  <span
                    key={t}
                    className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    </div>
  );
}

function prettifyCategory(slug) {
  if (!slug) return "Uncategorized";
  return slug
    .split(/[-_]/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

function Wiki() {
  const { slug } = useParams();
  return slug ? <WikiArticle slug={slug} /> : <WikiIndex />;
}

export default requireAuth(Wiki);
